import { useRef } from "react";
import { Swiper as SwiperType } from "swiper";
import { Mousewheel, Virtual } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import { useTranslate } from "@/providers/context";
import { ChevronLeftMD, ChevronRightMD } from "@/icons";
import { ContentThumbnail } from "@/components";
import { useNavigateToItem } from "@/hooks";
import { Video } from "@/api";
import { getContentThumbnailUrl } from "@/utils";

import { useCourseCompletionAll } from "../hooks/useCourseCompletionAll";

interface InProgressSliderProps {
  "data-testid"?: string;
  className?: string;
}

export const InProgressSlider = ({ ...props }: InProgressSliderProps) => {
  const { data, isLoading, error } = useCourseCompletionAll();
  const { translate } = useTranslate();
  const { navigateToItem } = useNavigateToItem();

  const swiperRef = useRef<SwiperType>();

  if (isLoading || error || !data) {
    return null;
  }
  const items = data?.courses;

  return (
    <div {...props}>
      <div className="mb-[12px] flex h-[38px] items-center justify-between">
        <div
          data-testid={`${props["data-testid"]}-title`}
          className="h5-bold flex-1 items-center overflow-hidden overflow-ellipsis whitespace-nowrap text-item-primary"
        >
          {translate("In Progress")}
        </div>
        {data?.courses?.length > 2 && (
          <div className="flex items-center">
            <button
              className="ml-[8px] flex h-[24px] w-[24px] items-center justify-center rounded-[50%] bg-surface-tertiary hover:bg-border"
              onClick={() => swiperRef.current?.slidePrev()}
            >
              <ChevronLeftMD className="h-[20px] w-[20px]" />
            </button>
            <button
              className="ml-[8px] flex h-[24px]  w-[24px] items-center justify-center rounded-[50%] bg-surface-tertiary hover:bg-border"
              onClick={() => swiperRef.current?.slideNext()}
            >
              <ChevronRightMD className="h-[20px] w-[20px]" />
            </button>
          </div>
        )}
      </div>
      <Swiper
        slidesPerView="auto"
        slidesPerGroup={1}
        spaceBetween={16}
        modules={[Mousewheel, Virtual]}
        mousewheel={{ forceToAxis: true }}
        onBeforeInit={(swiper) => {
          swiperRef.current = swiper;
        }}
      >
        {items?.map((item) => {
          const fullItem = item.next_video as Video;

          return (
            <SwiperSlide
              key={item.course_id}
              onClick={() => {
                navigateToItem({
                  parentId: item.next_video_parent_id,
                  courseId: item.course_id,
                  item: { ChildId: fullItem.VideoId.toString(), Type: "video" },
                  fullItem,
                });
              }}
              className="flex h-[125px] w-[704px] max-w-full cursor-pointer"
            >
              <div className="flex w-full rounded-[12px]">
                <ContentThumbnail
                  thumbnail={
                    item.course_thumbnails ? getContentThumbnailUrl({Thumbnails: item.course_thumbnails}, "medium") : ""
                  }
                  className="aspect-video w-[220px] flex-shrink-0 cursor-pointer rounded-l-[12px]"
                />
                <div className="flex w-[480px] flex-col rounded-r-[12px] bg-surface-secondary px-6  pb-5 pt-4">
                  <div className="h5-bold mb-4 line-clamp-2 w-full overflow-ellipsis">
                    {item.course_name}
                  </div>
                  <div className="p1 line-clamp-2 flex w-full justify-between overflow-ellipsis text-item-secondary">
                    <div>{item.next_video.Title}</div>
                    <div className="w-[84px] pl-3 text-right">
                      {item.percentage_completion}%
                    </div>
                  </div>
                  <div className="mt-4 h-2 w-full rounded-xl bg-brand-secondary-pressed">
                    <div
                      className="h-full rounded-full bg-brand-primary"
                      style={{width: `${item.percentage_completion}%`}}
                    />
                  </div>
                </div>
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>

    </div>
  );
};
